/**
 * What each record affordance requires, named once so every surface asking the same question shares
 * one rule. A rule whose role arm reads a fact only the call site holds — an ownership or assignment
 * check — is built there instead, since no constant can close over it.
 */
import { recordAuthorization, tprmLevelsSwitch } from './accessRules'
import { any, orgFlag } from './availability'
import {
  addsLibraryDocuments,
  creates,
  hasEntityAuthorization,
  managesConfigArea,
  managesPermissions,
  viewsAllDocuments,
  viewsAllRequests,
} from './fgaRules'
import {
  adminsOnly,
  everyRole,
  everyRoleExceptSuppliers,
  noRole,
  nonViewerAdmins,
  nonViewerAdminsUnlessP2pRestricted,
  nonViewOnly,
  superAdmin,
} from './roleAuthorization'
import type { NonRecordAccessRule, RecordAccessRule, TprmLevelsSwitch } from './accessRules'

export const EDIT_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEdit', nonViewerAdmins),
}

/** Renaming, and reparenting from the hierarchy: open to every role before the service answered. */
export const EDIT_AGREEMENT_ANY_ROLE: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEdit', everyRole),
}

export const CANCEL_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canCancel', nonViewerAdmins),
}

export const DELETE_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canDelete', nonViewerAdmins),
}

/** Administering an agreement — today, marking it confidential. */
export const ADMINISTER_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canAdminister', noRole),
}

export const EDIT_AGREEMENT_DOCUMENTS: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEditDocuments', nonViewOnly),
}

export const VIEW_REQUEST_RECEIPTS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canView', everyRoleExceptSuppliers),
}

/** Tags, budget and request leads. The owner is built where it is shown: a view-only owner may hand the request on. */
export const EDIT_REQUEST_METADATA: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', nonViewerAdmins),
}

/** The same verb; before the service answered, view-only admins could set the priority too. */
export const EDIT_REQUEST_PRIORITY: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', adminsOnly),
}

/** The same verb; before the service answered, any caller who could act could rename the request. */
export const RENAME_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', nonViewOnly),
}

/**
 * Wider than `EDIT_REQUEST_METADATA` on the same verb by a view-only administrator: the renewals
 * service admits one assigned to the renewal, and no assignment is visible here to narrow it by.
 */
export const EDIT_REQUEST_RENEWAL_DATE: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', adminsOnly),
}

export const EDIT_REQUEST_PO_NUMBER: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditPoNumber', adminsOnly),
}

export const EDIT_REQUEST_SUBMISSIONS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditSubmissions', nonViewerAdmins),
}

export const REJECT_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canReject', nonViewerAdmins),
}

export const DELETE_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canDelete', nonViewerAdminsUnlessP2pRestricted),
}

export const REOPEN_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canRollback', nonViewerAdmins),
}

export const TOGGLE_REQUEST_HIDDEN_STEPS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canAdminister', adminsOnly),
}

/** Opening a procedure run's audit entries from the request timeline: the audit log reads them under the request. */
export const VIEW_REQUEST_PROCEDURE_RUN_AUDIT: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canAdminister', adminsOnly),
}

/** Adding documents, editing their details, and adding or reverting versions. */
export const EDIT_REQUEST_DOCUMENTS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditDocuments', nonViewOnly),
}

export const DELETE_REQUEST_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageDocuments', everyRoleExceptSuppliers),
}

export const MANAGE_SOURCING: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageSourcing', everyRole),
}

export const EDIT_REQUEST_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditTprm', nonViewOnly),
}

export const MANAGE_REQUEST_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageTprm', nonViewOnly),
}

// TODO(PLT-2173): delete once RELEASE_TPRM_LEVELS is on for every organisation, and use the flagOn rule.
/** Adding a request's risks and editing them, as the risks service decides. */
export const EDIT_REQUEST_RISKS: TprmLevelsSwitch<RecordAccessRule> = tprmLevelsSwitch({
  flagOff: MANAGE_REQUEST_TPRM,
  flagOn: { authorization: recordAuthorization('request', 'canEditRisks', nonViewOnly) },
})

// TODO(PLT-2173): delete once RELEASE_TPRM_LEVELS is on for every organisation, and use the flagOn rule.
/** Deleting a request's risks, as the risks service decides. */
export const MANAGE_REQUEST_RISKS: TprmLevelsSwitch<RecordAccessRule> = tprmLevelsSwitch({
  flagOff: MANAGE_REQUEST_TPRM,
  flagOn: { authorization: recordAuthorization('request', 'canManageRisks', nonViewOnly) },
})

/** Also admits a view-only admin, as the artefacts service's role check does. */
export const EDIT_REQUEST_ARTEFACTS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditTprm', everyRoleExceptSuppliers),
}

/** Also admits a view-only admin, as the artefacts service's role check does. */
export const MANAGE_REQUEST_ARTEFACTS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageTprm', everyRoleExceptSuppliers),
}

/**
 * Narrower than the destination page's own rule: before the service answered, a request linked through
 * to its supplier and agreement for admins only.
 */
export const LINK_SUPPLIER_FROM_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canView', adminsOnly),
}

export const LINK_AGREEMENT_FROM_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canView', adminsOnly),
}

/** A link to a supplier's profile from a row that names it. Nobody off FGA, where such rows show the name alone. */
export const VIEW_SUPPLIER: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canView', noRole),
}

/** Naming a product that does not exist yet, which creates it on the chosen supplier. */
export const CREATE_SUPPLIER_PRODUCT: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canCreateProduct', everyRole),
}

export const VIEW_SUPPLIER_SUBMISSIONS: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canViewRelatedSubmissions', everyRole),
}

export const VIEW_SUPPLIER_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
}

export const EDIT_SUPPLIER_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canEditTprm', nonViewOnly),
}

export const MANAGE_SUPPLIER_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canManageTprm', nonViewOnly),
}

// TODO(PLT-2173): delete once RELEASE_TPRM_LEVELS is on for every organisation, and use the flagOn rule.
/** Adding a supplier's risks and editing them, as the risks service decides. */
export const EDIT_SUPPLIER_RISKS: TprmLevelsSwitch<RecordAccessRule> = tprmLevelsSwitch({
  flagOff: MANAGE_SUPPLIER_TPRM,
  flagOn: { authorization: recordAuthorization('supplier', 'canEditRisks', nonViewOnly) },
})

/** The TPRM sections of the supplier risk management tab. Off the service every role passes here, so the tab's gate decides. */
export const VIEW_SUPPLIER_RISK_MANAGEMENT_TPRM: RecordAccessRule = {
  availability: any([orgFlag('TPRM'), orgFlag('TPRM_CONTINUOUS_MONITORING'), orgFlag('ENABLE_ASSESSMENTS')]),
  authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
}

/** The same verb, narrowed to organisations that have the screening module the data comes from. */
export const VIEW_SUPPLIER_SCREENING_CASES: RecordAccessRule = {
  availability: orgFlag('TPRM_CONTINUOUS_MONITORING'),
  authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
}

const subprocessorsAvailability = orgFlag('SUPPLIER_SUBPROCESSORS')

export const VIEW_SUPPLIER_SUBPROCESSORS: TprmLevelsSwitch<RecordAccessRule> = tprmLevelsSwitch({
  flagOff: {
    availability: subprocessorsAvailability,
    authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
  },
  flagOn: {
    availability: subprocessorsAvailability,
    authorization: recordAuthorization('supplier', 'canViewSubprocessors', everyRole),
  },
})

export const EDIT_SUPPLIER_SUBPROCESSORS: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canEditSubprocessors', nonViewerAdmins),
}

/** Administering a supplier — its lifecycle settings (archive, active/inactive, merge) and confidentiality. */
export const ADMINISTER_SUPPLIER: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canAdminister', superAdmin),
}

/** Each check is a billable provider call, so off FGA it stays with admins who may act. */
export const VERIFY_SUPPLIER_BANK_ACCOUNT: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canAdminister', nonViewerAdmins),
}

export const CAN_ACCEPT_BILL: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canAccept', nonViewerAdmins),
}

export const CAN_APPROVE_BILL: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canApprove', nonViewerAdmins),
}

export const CAN_EDIT_BILL_BEFORE_ACCEPT: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canEditBeforeAccept', nonViewerAdmins),
}

export const CAN_ASSIGN_BILL: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canAssign', nonViewerAdmins),
}

/**
 * The contract email send admits acting admins by role and checks everyone else's request participation,
 * whatever the Permissions Service grants, so this asks the role alone; call sites add participation.
 */
export const SEND_CONTRACT_EMAIL_AS_ADMIN: NonRecordAccessRule = {
  authorization: { kind: 'role', role: nonViewerAdmins },
}

/** The unassign route admits admins by role, and no bill verb answers it, so this asks the role alone. */
export const UNASSIGN_BILL: NonRecordAccessRule = {
  authorization: { kind: 'role', role: nonViewerAdmins },
}

/** Permissions administrators may manage any record's access, so this asks the organisation, not the record. */
export const MANAGE_RECORD_ACCESS: NonRecordAccessRule = {
  authorization: { kind: 'fga', fga: managesPermissions },
}

/** A form's amendment request export holds reviewers' comments from every request, so it needs view access to all of them. */
export const EXPORT_AMENDMENT_REQUESTS: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: viewsAllRequests, role: adminsOnly },
}

export const CREATE_AGREEMENT: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: creates('agreement'), role: adminsOnly },
}

/**
 * Offering bulk edit on the agreements list. The service grants editing per agreement, never across the family, so
 * any agreement access offers it and `EDIT_AGREEMENT` decides each row.
 */
export const BULK_EDIT_AGREEMENTS: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('agreement'), role: nonViewerAdmins },
}

/** Rename, retype or edit the custom fields of one listed document. */
export const EDIT_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('document', 'canEdit', nonViewOnly),
}

/** Delete one listed document. */
export const MANAGE_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('document', 'canManage', nonViewOnly),
}

/**
 * Mark one document confidential and change who it is shared with. Nobody off FGA: the role rules ignore a document's
 * own mark and its access list, so offering either would promise a restriction nothing enforces.
 */
export const MANAGE_DOCUMENT_ACCESS: RecordAccessRule = {
  authorization: recordAuthorization('document', 'canManage', noRole),
}

/** Add new on the Documents page: a document with no parent needs the organisation documents level at edit. */
export const ADD_LIBRARY_DOCUMENT: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: addsLibraryDocuments, role: nonViewerAdmins },
}

/**
 * The search panel on the Documents page, matching the search endpoints: the organisation documents level
 * (`can_view_all_documents`), or an admin role before the service answers, in CLM organisations.
 */
export const SEARCH_DOCUMENTS: NonRecordAccessRule = {
  availability: orgFlag('SUPPLIER_CLM'),
  authorization: { kind: 'fgaOrRole', fga: viewsAllDocuments, role: adminsOnly },
}

/** Match the task-agent-config endpoint under both permission rollouts. */
export const TASK_AGENT_CONFIG_CHAT: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: managesConfigArea('workflows_and_forms'), role: superAdmin },
}
